import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Spin, Tag, message } from "antd";
import {
  CommentOutlined,
  GlobalOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import ProductLearnBanner from "./ProductLearnBanner";
import ChannelsSection, { ChannelIconRow } from "./ChannelsSection";
import HubSpotConnectCard from "./HubSpotConnectCard";
import {
  lastChatFromSessions,
  listProspectBots,
  listProspectSessions,
  listQualifiedProspects,
  getProspectLeadCount,
  prospectBotRagStatus,
} from "./prospectService";
import { formatLastChat } from "./lastChatDisplay";
import { channelConnection, getBotStatus, statusTagColor } from "./channelKind";
import { SONIC_PROSPECT_PRICING } from "util/prospectBilling";
import SonicProspectUpgradeModal from "./SonicProspectUpgradeModal";
import SalesAvailabilityPanel from "./SalesAvailabilityPanel";
import styles from "./style.module.scss";

const SHOPIFY_DISMISS_KEY = "sp-shopify-discovery-dismissed";

const BotList = ({
  bots,
  loading,
  onOpen,
  onAddChannel,
  onOpenBilling,
  onRefresh,
  onAssistantUpdated,
  whatsappConfig,
  user,
}) => {
  const { t } = useTranslation();
  const [lastChats, setLastChats] = useState({});
  const [leadCounts, setLeadCounts] = useState({});
  const [chatCounts, setChatCounts] = useState({});
  const [pricesOpen, setPricesOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [requestConnect, setRequestConnect] = useState("");
  const [monthQp, setMonthQp] = useState(0);
  const [freeUsed, setFreeUsed] = useState(0);
  const [shopifyHidden, setShopifyHidden] = useState(
    () => window.localStorage.getItem(SHOPIFY_DISMISS_KEY) === "1",
  );
  const assistant = useMemo(() => {
    const list = (bots || []).filter(Boolean);
    list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    return list[0] || null;
  }, [bots]);
  const freeLimit = SONIC_PROSPECT_PRICING.freeLifetimeQp;
  const ragStatusRef = useRef({});

  const hasActiveIndexSync = useMemo(
    () =>
      (bots || []).some((bot) => {
        const status = prospectBotRagStatus(bot);
        return status === "pending" || status === "processing";
      }),
    [bots],
  );

  useEffect(() => {
    const next = { ...ragStatusRef.current };
    for (const bot of bots || []) {
      if (bot?.id != null) {
        next[bot.id] = prospectBotRagStatus(bot);
      }
    }
    ragStatusRef.current = next;
  }, [bots]);

  useEffect(() => {
    if (!hasActiveIndexSync) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { bots: freshBots } = await listProspectBots();
        if (cancelled) return;
        const prev = ragStatusRef.current;
        for (const bot of freshBots || []) {
          const oldStatus = prev[bot.id];
          const newStatus = prospectBotRagStatus(bot);
          if (
            oldStatus &&
            (oldStatus === "pending" || oldStatus === "processing") &&
            newStatus === "ready"
          ) {
            message.success(
              t("sonic_prospect.notif_index_ready", {
                name: bot.name || t("sonic_prospect.preview_bot_fallback"),
              }),
            );
          }
          if (
            oldStatus &&
            (oldStatus === "pending" || oldStatus === "processing") &&
            newStatus === "failed"
          ) {
            message.error(
              t("sonic_prospect.notif_index_failed", {
                name: bot.name || t("sonic_prospect.preview_bot_fallback"),
              }),
            );
          }
        }
        ragStatusRef.current = Object.fromEntries(
          (freshBots || [])
            .filter((bot) => bot?.id != null)
            .map((bot) => [bot.id, prospectBotRagStatus(bot)]),
        );
        onRefresh();
      } catch (_) {
        /* ignore transient poll errors */
      }
    };
    const timer = setInterval(poll, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [hasActiveIndexSync, onRefresh, t]);

  const handleCreate = () => {
    if (!assistant) onAddChannel();
    else setConnectOpen(true);
  };

  const openChannel = (id) => {
    if (!assistant) return;
    setConnectOpen(false);
    if (id === "web") {
      const connection = channelConnection(assistant, "web");
      onOpen(assistant, connection.state === "pending" ? "embed" : "settings");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const ids = assistant?.id ? [assistant.id] : [];
    if (!ids.length) {
      setLastChats({});
      setLeadCounts({});
      setChatCounts({});
      return undefined;
    }
    Promise.all(
      ids.map(async (id) => {
        try {
          const [sessions, count] = await Promise.all([
            listProspectSessions(id),
            getProspectLeadCount(id),
          ]);
          return [id, {
            lastChatAt: lastChatFromSessions(sessions),
            leadCount: count,
            chatCount: Array.isArray(sessions) ? sessions.length : 0,
          }];
        } catch (_) {
          return [id, { lastChatAt: null, leadCount: 0 }];
        }
      })
    ).then((entries) => {
      if (!cancelled) {
        setLastChats(Object.fromEntries(entries.map(([id, v]) => [id, v.lastChatAt])));
        setLeadCounts(Object.fromEntries(entries.map(([id, v]) => [id, v.leadCount])));
        setChatCounts(Object.fromEntries(entries.map(([id, v]) => [id, v.chatCount])));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [assistant]);

  useEffect(() => {
    if (!assistant?.id) {
      setMonthQp(0);
      setFreeUsed(0);
      return undefined;
    }
    let cancelled = false;
    listQualifiedProspects(assistant.id, { limit: 1, offset: 0 })
      .then((res) => {
        if (cancelled || !res) return;
        setFreeUsed(Math.min(Number(res.orgCount || 0), freeLimit));
        setMonthQp(Number(res.spendProtection?.monthly_qp_count || 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [assistant, freeLimit]);

  const unconnected = assistant
    ? ["web", "whatsapp", "instagram"].filter(
        (id) => channelConnection(assistant, id).state !== "connected",
      )
    : [];

  return (
    <div className={styles.listPage}>
      <header className={styles.pageHeader}>
        <div>
          <h1>{assistant?.name || t("sonic_prospect.page_title")}</h1>
          <p>{t("sonic_prospect.page_subtitle")}</p>
        </div>
        <div className={styles.headerActions}>
          <Button onClick={() => setPricesOpen(true)}>
            {t("sonic_prospect.header_prices")}
          </Button>
          <Button onClick={onOpenBilling}>{t("sonic_prospect.header_billing")}</Button>
          <Button type="primary" icon={assistant ? <PlusOutlined /> : null} onClick={handleCreate}>
            {assistant
              ? t("sonic_prospect.header_connect_channel")
              : t("sonic_prospect.header_start")}
          </Button>
        </div>
      </header>

      {user?.uid ? (
        <SalesAvailabilityPanel userId={user.uid} onChanged={onRefresh} />
      ) : null}

      <ProductLearnBanner userId={user?.uid} />

      {loading ? (
        <div className={styles.centered}>
          <Spin size="large" />
        </div>
      ) : !assistant ? (
        <div className={styles.emptyHero}>
          <CommentOutlined className={styles.emptyIcon} />
          <h2>{t("sonic_prospect.empty_title")}</h2>
          <p>{t("sonic_prospect.empty_desc")}</p>
          <Button type="primary" size="large" onClick={handleCreate}>
            {t("sonic_prospect.header_start")}
          </Button>
        </div>
      ) : (
        <div className={styles.assistantOverview}>
          <div className={styles.channelTop}>
            <span className={styles.channelName}>
              <GlobalOutlined />
              <strong>{assistant.name}</strong>
            </span>
            <Tag color={statusTagColor(getBotStatus(assistant))}>
              {t(`sonic_prospect.channel_status_${getBotStatus(assistant)}`)}
            </Tag>
            <ChannelIconRow bot={assistant} t={t} />
          </div>
          <div className={styles.statStrip}>
            <span>
              {t("sonic_prospect.last_chat")}
              <strong>
                {formatLastChat(lastChats[assistant.id], t, "tr-TR").time ||
                  t("sonic_prospect.last_chat_empty")}
              </strong>
            </span>
            <span>
              {t("sonic_prospect.lead_count_label")}
              <strong>{leadCounts[assistant.id] ?? 0}</strong>
            </span>
            <span>
              {t("sonic_prospect.stat_chats")}
              <strong>{chatCounts[assistant.id] ?? 0}</strong>
            </span>
            <span>
              {t("sonic_prospect.stat_month_qp")}
              <strong>{monthQp}</strong>
            </span>
          </div>
          <div className={styles.coverageBlock}>
            <div className={styles.coverageLabel}>
              <span>{t("sonic_prospect.billing_free_label")}</span>
              <span>
                {freeUsed >= freeLimit
                  ? t("sonic_prospect.billing_free_done", {
                      price: new Intl.NumberFormat("tr-TR", {
                        style: "currency",
                        currency: "USD",
                      }).format(SONIC_PROSPECT_PRICING.tier1Price),
                    })
                  : t("sonic_prospect.billing_free_value", { used: freeUsed, max: freeLimit })}
              </span>
            </div>
            <div className={styles.coverageTrack} aria-hidden="true">
              <span style={{ width: `${(freeUsed / freeLimit) * 100}%` }} />
            </div>
          </div>
          <ChannelsSection
            bot={assistant}
            variant="dashboard"
            whatsappConfig={whatsappConfig}
            requestConnect={requestConnect}
            onRequestHandled={() => setRequestConnect("")}
            onOpenEmbed={() => onOpen(assistant, "embed")}
            onOpenSettings={() => onOpen(assistant, "settings")}
            onUpdated={(saved) => {
              if (onAssistantUpdated) onAssistantUpdated(saved);
              onRefresh();
            }}
          />
          <HubSpotConnectCard />
          {shopifyHidden ? null : (
            <article className={styles.shopifyDiscover}>
              <div>
                <h2>{t("sonic_prospect.shopify_card_title")}</h2>
                <p>{t("sonic_prospect.shopify_card_body")}</p>
                <p>
                  {t("sonic_prospect.billing_free_label")}: {" "}
                  {t("sonic_prospect.billing_free_value", { used: 0, max: freeLimit })}
                </p>
              </div>
              <div className={styles.headerActions}>
                <Button
                  type="primary"
                  href={SONIC_PROSPECT_PRICING.shopifyAppStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("sonic_prospect.shopify_install_cta")}
                </Button>
                <Button
                  type="link"
                  onClick={() => {
                    window.localStorage.setItem(SHOPIFY_DISMISS_KEY, "1");
                    setShopifyHidden(true);
                  }}
                >
                  {t("sonic_prospect.shopify_discover_dismiss")}
                </Button>
              </div>
            </article>
          )}
        </div>
      )}
      <Modal
        open={connectOpen}
        title={t("sonic_prospect.connect_dialog_title")}
        footer={null}
        onCancel={() => setConnectOpen(false)}
      >
        <div className={styles.connectChoices}>
          {unconnected.map((id) => (
            <Button
              key={id}
              block
              onClick={() => {
                setConnectOpen(false);
                if (id === "web") openChannel("web");
                else setRequestConnect(id);
              }}
            >
              {t(`sonic_prospect.channel_name_${id}`)}
            </Button>
          ))}
          <Button
            block
            href={SONIC_PROSPECT_PRICING.shopifyAppStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("sonic_prospect.connect_dialog_shopify")}
          </Button>
        </div>
      </Modal>
      <SonicProspectUpgradeModal open={pricesOpen} onClose={() => setPricesOpen(false)} />
    </div>
  );
};

export default BotList;
